import { Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { UserService } from '../../../../../modules/fwcore/services/user.service';
import { DealsService } from '../../../services/deals.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { ImpersonationService } from '../../../../../modules/fwcore/services/impersonation.service';
import { RequestDealCount, ResponseDealCount } from '../../../models/messages/deal-count-messages';
import { MaintenanceMsg } from '../../../../admin/models/messages/maintenance-msg-messages';
import { of, timer } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-pb-last-visit-summary',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './pb-last-visit-summary.component.html',
  styleUrl: './pb-last-visit-summary.component.scss'
})
export class PbLastVisitSummaryComponent {

  /** Company filter applied to the summary counts */
  public filterCompanyId = input<string | null>(null);

  protected helpUrl = this.userService.helpUrl;
  protected sharedDealCount = signal<number>(0);
  protected updatedTowerCount = signal<number>(0);

  /** The currently active maintenance message (null = none, pill hidden) */
  protected maintenanceMsg = signal<MaintenanceMsg | null>(null);

  protected destroyRef = inject(DestroyRef);

  /** How often the home page re-checks the maintenance message */
  private readonly maintenanceRefreshMs = 5 * 60 * 1000;   // 5 minutes

  constructor(private userService: UserService
    , private dealsService: DealsService
    , private impersonationService: ImpersonationService) {

    effect(() => {
      this.impersonationService.impersonating();
      this.filterCompanyId();
      this.loadSharedDealCount();
      this.loadUpdatedTowerCount();
    });

    // Same message for every user/company, so not inside the effect.
    this.loadMaintenanceMsg();
  }

  protected loadUpdatedTowerCount(): void {

    const req: RequestDealCount = {
      userCompanyId: this.filterCompanyId()
    };

    this.dealsService.getData$<RequestDealCount, ResponseDealCount>(req, this.dealsService.endPointTowerUpdateCountUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.updatedTowerCount.set(response.count ?? 0);
        },
        error: (err: any) => {
          console.error(err);
        }
      });
  }

  protected loadSharedDealCount(): void {

    const req: RequestDealCount = {
      userCompanyId: this.filterCompanyId()
    };

    this.dealsService.getData$<RequestDealCount, ResponseDealCount>(req, this.dealsService.endPointSharedDealCountUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.sharedDealCount.set(response.count ?? 0);
        },
        error: (err: any) => {
          console.error(err);
        }
      });
  }

  /**
   * Load the active maintenance message now, then re-check every 5 minutes.
   * The API only returns a message when isActive = 1 and the current UTC time
   * is between showAfter and expiresOn; otherwise null -> pill hidden.
   */
  protected loadMaintenanceMsg(): void {
    console.log('1. loadMaintenanceMsg called');
    timer(0, this.maintenanceRefreshMs)
      .pipe(
        tap(() => console.log('2. timer fired')),
        switchMap(() => {
          console.log('3. calling getActiveMaintenanceMsg');
          return this.dealsService.getData$<{}, MaintenanceMsg>({} , this.dealsService.endPointMaintenanceActivemsg)
            .pipe(
              catchError((err: any) => {
                console.error('API error', err);
                return of(null);
              })
            )
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (msg) => {
          console.log('4. response', msg);
          this.maintenanceMsg.set(msg?.displayMarkup ? msg : null);
        },
        error: (err) => console.error('5. stream error', err)
      });
  }

  private sanitizer = inject(DomSanitizer);

  protected maintenanceHtml = computed<SafeHtml | null>(() => {
    const html = this.maintenanceMsgCleanHtml(this.maintenanceMsg()?.displayMarkup);
    return html ? this.sanitizer.bypassSecurityTrustHtml(html) : null;
  });

  protected maintenanceText = computed(() => this.maintenanceMsgToText(this.maintenanceMsg()?.displayMarkup));

  protected maintenanceMsgToText(html?: string | null): string {
  if (!html) return '';
  return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '';
  }

  /**
 * Removes anything that could run code from the message HTML,
 * but keeps formatting (b, i, u, span style="color", links).
 */
  protected maintenanceMsgCleanHtml(html?: string | null): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // 1. remove dangerous elements completely
  doc.body.querySelectorAll('script, style, iframe, object, embed, link, meta, form, input, button')
    .forEach(el => el.remove());

  // 2. remove dangerous attributes from every remaining element
  doc.body.querySelectorAll('*').forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();

      if (name.startsWith('on')                                   // onclick, onerror, ...
        || ((name === 'href' || name === 'src') && value.startsWith('javascript:'))) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

}
